// FILENAME: frontend/src/components/HistoricalTrends.jsx
import React, { useState, useMemo } from 'react';
import { Box, Typography, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem, Paper, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { format, parseISO } from 'date-fns';
import useApi from '../hooks/useApi';
import AnimatedChart from './AnimatedChart';

const PROGRAMS = [
    'Website', 'Chat', 'Trade', 'SEO',
    'Digital Ad Sales', 'Digital Ad Service'
];

const CENTRAL_TIMEZONE = 'America/Chicago';

const getSnapshotTimestamp = (snapshot) => {
    const raw = snapshot?.createdAt || snapshot?.updatedAt || snapshot?.date;
    if (!raw) return null;
    // Backend values are not always strict ISO; fall back to native Date parsing.
    try {
        const parsedIso = parseISO(String(raw));
        if (!Number.isNaN(parsedIso.getTime())) return parsedIso;
    } catch (_err) {
        // noop
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatCentralDayKey = (date) => date.toLocaleDateString('en-CA', { timeZone: CENTRAL_TIMEZONE });

const coerceEnrollmentCount = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const n = typeof value === 'number' ? value : Number(String(value).trim());
    return Number.isFinite(n) ? n : null;
};

const buildDailySnapshotPoints = (snapshots = [], programName) => {
    const daily = new Map();

    for (const snapshot of snapshots) {
        if (snapshot?.programName !== programName) continue;

        const enrollmentCount =
            coerceEnrollmentCount(snapshot?.enrollmentCount) ??
            coerceEnrollmentCount(snapshot?.count) ??
            coerceEnrollmentCount(snapshot?.total) ??
            coerceEnrollmentCount(snapshot?.rowCount);
        if (enrollmentCount === null) continue;

        const timestamp = getSnapshotTimestamp(snapshot);
        if (!timestamp) continue;

        const dayKey = formatCentralDayKey(timestamp);
        const existing = daily.get(dayKey);

        if (!existing || timestamp.getTime() > existing.timestamp.getTime()) {
            daily.set(dayKey, { snapshot: { ...snapshot, enrollmentCount }, timestamp });
        }
    }

    return Array.from(daily.values()).sort((a, b) => a.timestamp - b.timestamp);
};

const HistoricalTrends = () => {
    const { useSnapshots } = useApi();
    const { data: snapshots, isLoading, error } = useSnapshots();
    const [selectedProgram, setSelectedProgram] = useState(PROGRAMS[0]);
    const [windowDays, setWindowDays] = useState(90);

    const chartData = useMemo(() => {
        if (!snapshots) return null;

        const dailyPoints = buildDailySnapshotPoints(snapshots, selectedProgram);
        if (dailyPoints.length === 0) return null;

        const cutoff = windowDays && Number.isFinite(windowDays)
            ? dailyPoints.filter((p) => (Date.now() - p.timestamp.getTime()) <= windowDays * 24 * 60 * 60 * 1000)
            : dailyPoints;
        const points = cutoff.length >= 2 ? cutoff : dailyPoints;

        const dates = points.map((entry) => entry.timestamp);
        const enrollmentCounts = points.map((entry) => entry.snapshot.enrollmentCount);

        return {
            dates,
            series: [{
                data: enrollmentCounts,
                label: 'Total Enrollments',
                color: '#3b82f6',
            }],
        };
    }, [snapshots, selectedProgram, windowDays]);

    if (isLoading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error.message}</Alert>;
    if (!snapshots || snapshots.length === 0) {
        return <Alert severity="info">No historical data available yet. The first snapshot will be generated automatically overnight.</Alert>;
    }

    return (
        <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Enrollment Trends</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <ToggleButtonGroup
                        size="small"
                        value={windowDays}
                        exclusive
                        onChange={(_e, next) => {
                            if (next === null) return;
                            setWindowDays(next);
                        }}
                        aria-label="trend window"
                    >
                        <ToggleButton value={30}>30d</ToggleButton>
                        <ToggleButton value={90}>90d</ToggleButton>
                        <ToggleButton value={365}>1y</ToggleButton>
                        <ToggleButton value={0}>All</ToggleButton>
                    </ToggleButtonGroup>
                    <FormControl sx={{ minWidth: 220 }} size="small">
                        <InputLabel>Program</InputLabel>
                        <Select
                            value={selectedProgram}
                            label="Program"
                            onChange={(e) => setSelectedProgram(e.target.value)}
                        >
                            {PROGRAMS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {chartData ? (
                <Box sx={{ height: 400 }}>
                    <AnimatedChart animationKey={`${selectedProgram}:${windowDays}:${chartData.dates.length}`}>
                        <LineChart
                            height={360}
                            xAxis={[{
                                data: chartData.dates,
                                scaleType: 'time',
                                valueFormatter: (date) => format(date, 'MMM d'),
                            }]}
                            series={chartData.series}
                            margin={{ left: 80 }}
                        />
                    </AnimatedChart>
                </Box>
            ) : (
                <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography>No data for the selected program.</Typography>
                </Box>
            )}
        </Paper>
    );
};

export default HistoricalTrends;
